import React, { useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useWorldRenderer, JumpTrigger } from './useWorldRenderer';
import { Point } from '../../types/point';

export interface Viewport { x: number; y: number; zoom: number; }

export interface WorldCanvasHandle {
  triggerJump: JumpTrigger;
  predictMove: (pointId: number, x: number, y: number) => void;
  /** Underlying PIXI <canvas>, for pixel read-back (e.g. timelapse recording). */
  getCanvas: () => HTMLCanvasElement | null;
}

interface WorldCanvasProps {
  points            : Point[];
  viewport          : Viewport;
  width             : number;
  height            : number;
  onViewportChange? : (vp: Viewport) => void;
  onLongPress?      : (worldX: number, worldY: number) => void;
  onWorldClick?     : (worldX: number, worldY: number) => void;
  onWorldHover?     : (worldX: number, worldY: number) => void;
  /** Keep the WebGL drawing buffer so the canvas can be read back each frame. */
  preserveDrawingBuffer? : boolean;
}

/**
 * Pan & zoom formulas (Y-up world, renderer draws at -point.y)
 * ─────────────────────────────────────────────────────────────
 * Screen transform:
 *   screen_x =  (world_x − vp.x) × zoom + w/2
 *   screen_y = −(world_y − vp.y) × zoom + h/2
 *
 * Drag by (dx, dy) — "world sticks to finger":
 *   world under cursor stays fixed → new_vp.x = vp.x − dx/zoom
 *                                    new_vp.y = vp.y + dy/zoom
 *   (drag up: dy<0 → vp.y decreases → see lower world-Y → content moves up ✓)
 *
 * Wheel / pinch — zoom toward cursor (mx, my):
 *   world under cursor:  worldX = (mx − w/2)/zoom + vp.x
 *                        worldY =  vp.y − (my − h/2)/zoom
 *   new vp that keeps it fixed:
 *     new_vp.x = worldX − (mx − w/2)/newZoom
 *     new_vp.y = worldY + (my − h/2)/newZoom
 */
export const WorldCanvas = forwardRef<WorldCanvasHandle, WorldCanvasProps>(({
  points, viewport, width, height, onViewportChange, onLongPress, onWorldClick, onWorldHover,
  preserveDrawingBuffer,
}, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Always-current refs — event handlers close over these,
  // so the listeners never need to be re-registered.
  const vpRef        = useRef(viewport);
  vpRef.current      = viewport;
  const szRef        = useRef({ width, height });
  szRef.current      = { width, height };
  const cbRef        = useRef(onViewportChange);
  cbRef.current      = onViewportChange;
  const cbLongPress  = useRef(onLongPress);
  cbLongPress.current = onLongPress;
  const cbWorldClick = useRef(onWorldClick);
  cbWorldClick.current = onWorldClick;
  const cbWorldHover = useRef(onWorldHover);
  cbWorldHover.current = onWorldHover;

  const isDragging      = useRef(false);
  const lastPointer     = useRef({ x: 0, y: 0 });
  const dragDistanceSq  = useRef(0);
  const lastPinch       = useRef<number | null>(null);
  const longPressTimer  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressOrigin = useRef<{ x: number; y: number } | null>(null);
  const longPressFired  = useRef(false);

  const { triggerJump, imperativeMove, getCanvas } = useWorldRenderer(
    containerRef, points, viewport, width, height, { preserveDrawingBuffer },
  );
  useImperativeHandle(ref, () => ({ triggerJump, predictMove: imperativeMove, getCanvas }), [triggerJump, imperativeMove, getCanvas]);

  // ── Mouse drag ─────────────────────────────────────────────────
  const onMouseDown = useCallback((e: MouseEvent) => {
    if (e.button !== 0) return;
    isDragging.current  = true;
    dragDistanceSq.current = 0;
    lastPointer.current = { x: e.clientX, y: e.clientY };
    if (containerRef.current) containerRef.current.style.cursor = 'grabbing';
    e.preventDefault();
  }, []);

  const onMouseMove = useCallback((e: MouseEvent) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      const { x: vpX, y: vpY, zoom } = vpRef.current;
      const { width: w, height: h } = szRef.current;
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const worldX = (mx - w / 2) / zoom + vpX;
      const worldY = vpY - (my - h / 2) / zoom;
      cbWorldHover.current?.(worldX, worldY);
    }
    if (!isDragging.current) return;
    const dx = e.clientX - lastPointer.current.x;
    const dy = e.clientY - lastPointer.current.y;
    dragDistanceSq.current += dx * dx + dy * dy;
    lastPointer.current = { x: e.clientX, y: e.clientY };
    const { x, y, zoom } = vpRef.current;
    // x: drag-right → vp.x decreases (see content to the left)  → x − dx/zoom
    // y: drag-up (dy<0) → vp.y decreases (see lower world-Y)    → y + dy/zoom
    cbRef.current?.({ zoom, x: x - dx / zoom, y: y + dy / zoom });
  }, []);

  const emitWorldClick = useCallback((clientX: number, clientY: number) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const { x: vpX, y: vpY, zoom } = vpRef.current;
    const { width: w, height: h } = szRef.current;
    const mx = clientX - rect.left;
    const my = clientY - rect.top;
    const worldX = (mx - w / 2) / zoom + vpX;
    const worldY = vpY - (my - h / 2) / zoom;
    cbWorldClick.current?.(worldX, worldY);
  }, []);

  const onMouseUp = useCallback((e: MouseEvent) => {
    const wasDragging = isDragging.current;
    const shouldClick = wasDragging && dragDistanceSq.current <= 16;
    isDragging.current = false;
    if (containerRef.current) containerRef.current.style.cursor = 'grab';
    if (shouldClick && !longPressFired.current) emitWorldClick(e.clientX, e.clientY);
    dragDistanceSq.current = 0;
  }, [emitWorldClick]);

  // ── Wheel zoom toward cursor ────────────────────────────────────
  const onWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    const { x: vpX, y: vpY, zoom } = vpRef.current;
    const { width: w, height: h }  = szRef.current;

    const factor  = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    const newZoom = Math.max(0.5, Math.min(1000, zoom * factor));

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    const worldX = (mx - w / 2) / zoom + vpX;
    const worldY = vpY - (my - h / 2) / zoom;          // Y-up: note the minus

    cbRef.current?.({
      zoom: newZoom,
      x   : worldX - (mx - w / 2) / newZoom,
      y   : worldY + (my - h / 2) / newZoom,           // Y-up: note the plus
    });
  }, []);

  // ── Touch: single-finger pan, two-finger pinch-zoom ────────────
  const cancelLongPress = useCallback(() => {
    if (longPressTimer.current !== null) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    longPressOrigin.current = null;
  }, []);

  const onTouchStart = useCallback((e: TouchEvent) => {
    e.preventDefault();
    if (e.touches.length === 1) {
      isDragging.current  = true;
      longPressFired.current = false;
      dragDistanceSq.current = 0;
      const touch = e.touches[0];
      lastPointer.current     = { x: touch.clientX, y: touch.clientY };
      longPressOrigin.current = { x: touch.clientX, y: touch.clientY };
      lastPinch.current       = null;

      longPressTimer.current = setTimeout(() => {
        longPressTimer.current = null;
        const origin = longPressOrigin.current;
        if (!origin) return;
        longPressFired.current = true;
        isDragging.current = false;
        const { x: vpX, y: vpY, zoom } = vpRef.current;
        const { width: w, height: h }  = szRef.current;
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return;
        const mx     = origin.x - rect.left;
        const my     = origin.y - rect.top;
        const worldX = (mx - w / 2) / zoom + vpX;
        const worldY = vpY - (my - h / 2) / zoom;
        cbLongPress.current?.(worldX, worldY);
      }, 500);
    } else if (e.touches.length === 2) {
      cancelLongPress();
      isDragging.current = false;
      lastPinch.current  = Math.hypot(
        e.touches[1].clientX - e.touches[0].clientX,
        e.touches[1].clientY - e.touches[0].clientY,
      );
    }
  }, [cancelLongPress]);

  const onTouchMove = useCallback((e: TouchEvent) => {
    e.preventDefault();
    const { x: vpX, y: vpY, zoom } = vpRef.current;
    const { width: w, height: h }  = szRef.current;

    if (e.touches.length === 1 && isDragging.current) {
      const touch = e.touches[0];
      // Cancel long press if finger moved more than 8px
      if (longPressOrigin.current) {
        const dx = Math.abs(touch.clientX - longPressOrigin.current.x);
        const dy = Math.abs(touch.clientY - longPressOrigin.current.y);
        if (dx > 8 || dy > 8) cancelLongPress();
      }
      const dx = touch.clientX - lastPointer.current.x;
      const dy = touch.clientY - lastPointer.current.y;
      dragDistanceSq.current += dx * dx + dy * dy;
      lastPointer.current = { x: touch.clientX, y: touch.clientY };
      cbRef.current?.({ zoom, x: vpX - dx / zoom, y: vpY + dy / zoom });

    } else if (e.touches.length === 2 && lastPinch.current !== null) {
      cancelLongPress();
      const newDist = Math.hypot(
        e.touches[1].clientX - e.touches[0].clientX,
        e.touches[1].clientY - e.touches[0].clientY,
      );
      const factor      = newDist / lastPinch.current;
      lastPinch.current = newDist;
      const newZoom     = Math.max(0.5, Math.min(1000, zoom * factor));

      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const mx     = (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left;
      const my     = (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top;
      const worldX = (mx - w / 2) / zoom + vpX;
      const worldY = vpY - (my - h / 2) / zoom;

      cbRef.current?.({
        zoom: newZoom,
        x   : worldX - (mx - w / 2) / newZoom,
        y   : worldY + (my - h / 2) / newZoom,
      });
    }
  }, [cancelLongPress]);

  const onTouchEnd = useCallback(() => {
    const shouldClick = dragDistanceSq.current <= 16 && !longPressFired.current;
    const origin = longPressOrigin.current;
    cancelLongPress();
    isDragging.current = false;
    lastPinch.current  = null;
    dragDistanceSq.current = 0;
    if (shouldClick && origin) emitWorldClick(origin.x, origin.y);
    longPressFired.current = false;
  }, [cancelLongPress, emitWorldClick]);

  // ── Register listeners once (all values read via refs) ─────────
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    el.style.cursor = 'grab';

    el.addEventListener('mousedown',  onMouseDown);
    window.addEventListener('mousemove', onMouseMove);   // window: drag works outside div
    window.addEventListener('mouseup',   onMouseUp);
    el.addEventListener('wheel',      onWheel,      { passive: false });
    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove',  onTouchMove,  { passive: false });
    el.addEventListener('touchend',   onTouchEnd);

    return () => {
      el.removeEventListener('mousedown',  onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup',   onMouseUp);
      el.removeEventListener('wheel',      onWheel);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove',  onTouchMove);
      el.removeEventListener('touchend',   onTouchEnd);
    };
  }, []); // empty — all current values accessed via refs, no re-registration needed

  return (
    <div
      ref={containerRef}
      style={{ width, height, overflow: 'hidden', position: 'relative' }}
    />
  );
});
